function updateYgHeaderCartCount(count) {
  if (typeof count !== 'number') return;

  document.querySelectorAll('.yg-lw-hd__cart-badge, .ui-hd__cart-badge').forEach((badge) => {
    badge.textContent = count;
    badge.hidden = count < 1;
  });

  document.querySelectorAll('.ui-hd__cart').forEach((cartLink) => {
    if (cartLink.querySelector('.ui-hd__cart-badge') || count < 1) return;
    const badge = document.createElement('span');
    badge.className = 'ui-hd__cart-badge';
    badge.textContent = count;
    cartLink.appendChild(badge);
  });

  document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((badge) => {
    badge.textContent = count;
  });
}

window.YGUpdateCartCount = updateYgHeaderCartCount;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setDelegatedHeaderCartAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .yg-lw-hd__cart, .ui-hd__cart, [data-yg-cart-drawer-trigger]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (cartLink.__cartDrawerLinked) return;
      cartLink.__cartDrawerLinked = true;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', 'false');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  setDelegatedHeaderCartAccessibility() {
    if (window.__ygCartDrawerDelegated) return;
    window.__ygCartDrawerDelegated = true;

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('.yg-lw-hd__cart, .ui-hd__cart, [data-yg-cart-drawer-trigger]');
      if (!cartLink) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', 'false');
      drawer.open(cartLink);
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      const cartLink = event.target.closest('.yg-lw-hd__cart, .ui-hd__cart, [data-yg-cart-drawer-trigger]');
      if (!cartLink) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      drawer.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (this.__ygCloseTimer) {
      clearTimeout(this.__ygCloseTimer);
      this.__ygCloseTimer = null;
    }
    this.classList.remove('is-closing');

    // Close any open header search first — otherwise a still-open search bar
    // overlaps the cart drawer. This theme's active header (yg-lw-header) uses
    // a custom .yg-search overlay driven by assets/yg-home.js (an `.is-open`
    // class + a body `yg-search-open` class), NOT Dawn's <details-modal>.
    // Click its own close control so yg-home.js runs its normal teardown; fall
    // back to toggling the classes directly if the control isn't found. Also
    // handle Dawn's <details-modal> in case a different header is ever used.
    document.querySelectorAll('[data-yg-search].is-open').forEach((overlay) => {
      const closeBtn = overlay.querySelector('[data-yg-search-close]');
      if (closeBtn) {
        closeBtn.click();
      } else {
        overlay.classList.remove('is-open');
        overlay.hidden = true;
        document.body.classList.remove('yg-search-open');
      }
    });
    document
      .querySelectorAll('details-modal[open], details.header__search[open], .header__search details[open]')
      .forEach((detailsEl) => {
        const d = detailsEl.matches('details') ? detailsEl : detailsEl.querySelector('details');
        if (d && !this.contains(d)) d.removeAttribute('open');
      });

    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (triggeredBy) triggeredBy.setAttribute('aria-expanded', 'true');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (this.__ygCloseTimer) clearTimeout(this.__ygCloseTimer);
    this.classList.add('is-closing');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
    if (this.activeElement) this.activeElement.setAttribute('aria-expanded', 'false');
    this.__ygCloseTimer = setTimeout(() => {
      this.classList.remove('is-closing');
      this.__ygCloseTimer = null;
    }, 340);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    updateYgHeaderCartCount(parsedState.item_count);
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // NOTE: the stock Dawn list also renders a 'cart-icon-bubble' section,
    // but the Love Wellness header rebuild replaced that element with
    // .yg-lw-hd__cart and there is no #cart-icon-bubble in the DOM. Dawn's
    // updateQuantity() does `document.getElementById(section.id).querySelector(...)`
    // with no null-guard, so keeping it here threw a TypeError on every
    // quantity change / remove — surfacing as "error while updating your
    // cart". The header cart count is kept in sync separately via
    // window.YGUpdateCartCount (see updateYgHeaderCartCount above), so this
    // section isn't needed. Only render what actually exists in the DOM.
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (typeof event?.cartData?.item_count === 'number') {
      updateYgHeaderCartCount(event.cartData.item_count);
      return;
    }

    const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
    fetch(`${cartUrl}.json`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cartData) => updateYgHeaderCartCount(cartData?.item_count))
      .catch(() => {});
  });
}
